import Fuse from 'fuse.js'
import { isValid } from 'date-fns'

import Model, { Request } from '../model'
import APIObject from '../object'
import Enum from '../enums'
import { DateYMD } from '../dates'
import { LocalFilterController, Filter, RemoteFilter, HybridFilter, FilterCategory, FilterOptions } from '../filter'

import { plugin as $date } from '../../plugins/date'

import DateRangePresets from '../../components/fields/presets/DateRange'

import CustomerCreditCard from './customerCreditCard'
import CustomerNote from './customerNote'
import CustomerPass from './customerPass'
import CustomField from './customField'
import CustomerForm from './customerForm'
import { EmployeeEmployeeType } from './employee'
import GiftCard from './giftCard'
import Order from './order'
import Pass from './pass'
import Photo from './photo'
import Product from './product'
import Reservation from './reservation'
import Segment from './segment'
import Upload from './upload'
import Waitlist from './waitlist'
import { CustomerCourse } from './course'

export default class Customer extends Model {
  static modelName () {
    return 'customer'
  }

  objectID () {
    return this.idCustomer || this.id
  }

  relations () {
    return {
      alerts: { type: CustomerNote },
      customerNotes: { type: CustomerNote },
      customFields: { type: CustomField },
      dateOfBirth: { type: DateYMD },
      gender: { type: CustomerGender },
      profilePhoto: { type: Photo },
      sendNotifications: { type: CustomerNotifications },
      stats: { type: CustomerStats },
      consentAccepted: { type: CustomerPrivacyConsent },
      marketingConsentAccepted: { type: CustomerMarketingConsent },
      massEmailUnsubscribed: { type: CustomerMassEmail },
      massSMSUnsubscribed: { type: CustomerMassSMS },
      segments: { type: Segment },
      deleted: { type: CustomerDeleted },
    }
  }

  static filter (filters) {
    const url = this.modelBaseURL() + '/filter'
    const request = {
      filters,
    }

    return this.requestItem(Request.jsonPost(url, request), CustomerFilterResponse)
  }

  /* static ids (ids, page = 1) {
    const url = this.modelBaseURL() + '/ids?page=' + page
    return this.requestList(Request.jsonPost(url, JSON.stringify(ids)), this)
  } */

  static ids (ids, page = 1) {
    const url = this.modelBaseURL() + '/ids?page=' + page

    const data = {
      ids,
    }

    return this.requestList(Request.jsonPost(url, data), this)
  }

  static listIDs (ids) {
    // use a default runner (to call the list method)
    const that = this

    const runner = (page) => {
      return that.ids(ids, page)
    }

    return this.listRunner(runner)
  }

  erase () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/erase'
    return this.constructor.requestSuccess(Request.get(url))
  }

  static updateSegments ({ customers, add, remove }) {
    const url = this.modelBaseURL() + '/updateSegments'
    const request = {
      customers: customers.map(customer => customer.idCustomer),
      add: add.map(segment => segment.idSegment),
      remove: remove.map(segment => segment.idSegment),
    }

    return this.requestSuccess(Request.jsonPost(url, request))
  }

  reservations ({ page = 1, limit = 50 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/reservations' + '?page=' + page + '&limit=' + limit
    return this.constructor.requestList(Request.get(url), Reservation)
  }

  upcomingReservations ({ page = 1, limit = 50 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/upcomingReservations' + '?page=' + page + '&limit=' + limit
    return this.constructor.requestList(Request.get(url), Reservation)
  }

  pastReservations ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/pastReservations' + '?page=' + page
    return this.constructor.requestList(Request.get(url), Reservation)
  }

  waitlists ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/waitlists' + '?page=' + page + '&filter=active'
    return this.constructor.requestList(Request.get(url), Waitlist)
  }

  unpaidReservations () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/unpaidReservations'
    return this.constructor.requestList(Request.get(url), Reservation)
  }

  combinedNotes ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/combinedNotes' + '?page=' + page
    return this.constructor.requestList(Request.get(url), CustomerCombinedNote)
  }

  orders ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/orders' + '?page=' + page
    return this.constructor.requestList(Request.get(url), Order)
  }

  forms ({ page = 1, limit = 10, includeItems = false } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/forms' + '?page=' + page + '&limit=' + limit + '&includeItems=' + (includeItems ? 1 : 0)
    return this.constructor.requestList(Request.get(url), CustomerForm)
  }

  cardsPasses () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/cardsPasses'
    return this.constructor.requestList(Request.get(url), CustomerCardsPasses)
  }

  passes ({ unpaid = false, page = 1 } = {}) {
    let url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/passes' + '?page=' + page

    if (unpaid) {
      url += '&filter=unpaid'
    }

    return this.constructor.requestList(Request.get(url), CustomerPass)
  }

  courses ({ page = 1, limit = 10 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/courses' + '?page=' + page + '&limit' + limit
    return this.constructor.requestList(Request.get(url), CustomerCourse)
  }

  giftCards ({ unpaid = false } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/giftCards' + '?unpaid=' + (unpaid ? 1 : 0)
    return this.constructor.requestList(Request.get(url), GiftCard)
  }

  creditCards ({ unpaid = false } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/creditCards'
    return this.constructor.requestList(Request.get(url), CustomerCreditCard)
  }

  creditTransactions ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/creditTransactions' + '?page=' + page
    return this.constructor.requestList(Request.get(url), CustomerCreditTransaction)
  }

  loyaltyTransactions ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/loyaltyTransactions' + '?page=' + page
    return this.constructor.requestList(Request.get(url), LoyaltyTransaction)
  }

  updateLoyaltyPointBalance ({ loyaltyPointBalance }) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID()
    const data = { loyaltyPointBalance }
    return this.constructor.requestItem(Request.jsonPut(url, data), this.constructor)
  }

  recalculateLoyaltyPointBalance () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/recalculateLoyaltyPoints'
    return this.constructor.requestSuccess(Request.jsonPost(url), this.constructor)
  }

  updateCreditBalance ({ creditBalance }) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID()
    const data = { creditBalance }
    return this.constructor.requestItem(Request.jsonPut(url, data), this.constructor)
  }

  productHistory () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/productHistory'
    return this.constructor.requestList(Request.get(url), ProductHistory)
  }

  paymentMethods () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/paymentMethods'
    return this.constructor.requestList(Request.get(url), CustomerPaymentMethods)
  }

  uploads ({ page = 1 } = {}) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/uploads' + '?page=' + page
    return this.constructor.requestList(Request.get(url), Upload)
  }

  getPhotos () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/photos'
    return this.constructor.requestList(Request.get(url), Photo)
  }

  addPhoto ({ photo, caption }, options) {
    const url = `${this.constructor.modelBaseURL()}/${this.objectID()}/addPhoto`

    const formData = new FormData()
    formData.append('photo', photo)
    formData.append('caption', caption)

    const request = Request.jsonPost(url, formData)
    request.options = options

    return this.constructor.requestItem(request, Photo)
  }

  static deletePhoto (photo) {
    const url = `${this.modelBaseURL()}/${photo.objectID()}/deletePhoto`
    return this.requestSuccess(Request.get(url))
  }

  static editPhoto (photo) {
    const url = this.modelBaseURL() + '/' + photo.objectID() + '/editPhoto'
    return this.requestItem(Request.jsonPost(url, photo.requestJSON()), Photo)
  }

  setProfilePhoto (photo) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/setProfilePhoto' + '?photo=' + photo.idPhoto
    return this.constructor.requestItem(Request.jsonPost(url), Customer)
  }

  removeProfilePhoto () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/removeProfilePhoto'
    return this.constructor.requestItem(Request.jsonPost(url), Customer)
  }

  merge ({ customers }) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/merge'
    const request = {
      customers: customers.map((customer) => {
        return customer.idCustomer
      }),
    }
    return this.constructor.requestSuccess(Request.jsonPost(url, request))
  }

  deleteCreditCard ({ creditCard }) {
    const url = this.constructor.modelBaseURL() + '/' + creditCard.objectID() + '/deleteCreditCard'
    return this.constructor.requestSuccess(Request.get(url))
  }

  resendWelcomeEmail () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/resendWelcomeEmail'
    return this.constructor.requestSuccess(Request.get(url))
  }

  resetPassword ({ password, confirm, sendEmail }) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/resetPassword' + '?password=' + password + '&confirm=' + confirm + '&sendEmail=' + (sendEmail ? 1 : 0)
    return this.constructor.requestSuccess(Request.get(url))
  }

  exportData () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/exportData'
    return this.constructor.requestSuccess(Request.get(url))
  }

  archive () {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/archive'
    return this.constructor.requestSuccess(Request.get(url))
  }

  verifyAge (ageVerifiedDetail) {
    const url = this.constructor.modelBaseURL() + '/' + this.objectID() + '/verifyAge'
    return this.constructor.requestItem(Request.jsonPost(url, ageVerifiedDetail), this.constructor)
  }

  static checkDuplicate ({ page = 1, filters = {}, limit = 20 } = {}) {
    const url = this.modelBaseURL() + '/list?page=' + page + '&limit=' + limit
    const request = {
      ...filters,
    }

    return this.requestList(Request.jsonPost(url, request), this)
  }

  static bulkArchive (customers, { restore = false } = {}) {
    const url = this.modelBaseURL() + '/bulkArchive?restore=' + (restore ? 1 : 0)

    const data = {
      customers: customers.map(({ idCustomer }) => idCustomer),
    }
    return this.requestSuccess(Request.jsonPost(url, data), this)
  }

  get addressLabel () {
    const { address, address2, city, country, postcode, region, suburb } = this
    const items = [address, address2, city, country, postcode, region, suburb]
    let label = ''

    for (const item of items) {
      if (item && item.length) {
        label += `${item} `
      }
    }

    if (label.length) {
      return label
    }

    return null
  }

  get image () {
    if (this.profilePhoto) {
      return this.profilePhoto.imageUrls.square
    }

    return null
  }

  get initials () {
    return ((this.firstName ? this.firstName.charAt(0) : '') + (this.lastName ? this.lastName.charAt(0) : '')).toUpperCase()
  }

  get isCustomerPrivacyConsentAccepted () {
    return this.consentAccepted === CustomerPrivacyConsent.accepted
  }

  get isArchived () {
    return this.deleted === CustomerDeleted.archived
  }

  get isDeleted () {
    return this.deleted === CustomerDeleted.deleted
  }

  get isBirthdayToday () {
    const today = new Date()
    const dateOfBirth = this.dateOfBirth?.value

    if (!dateOfBirth) { return false }

    const date = $date.parse(dateOfBirth)

    return (
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    )
  }

  get age () {
    const dobValue = this.dateOfBirth?.value
    if (!dobValue) {
      return null
    }

    const dob = $date.parse(dobValue)
    if (isNaN(dob.getTime())) {
      return null
    }

    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()

    const isBirthdayPassedThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate())

    if (!isBirthdayPassedThisYear) {
      age -= 1
    }

    return age
  }
}

export class CustomerCombinedNote extends APIObject {
  relations () {
    return {
      // reservation: { type: Reservation },
      customerNote: { type: CustomerNote },
      type: { type: CombinedNoteType },
    }
  }

  get isTypeCustomerNote () {
    return this.type === CombinedNoteType.customerNote
  }

  get isTypeReservation () {
    return this.type === CombinedNoteType.reservation
  }

  get name () {
    return this.type + ': ' + this.content
  }
}

export class CustomerPaymentMethods extends APIObject {
  relations () {
    return {
      giftCards: { type: GiftCard },
      creditCards: { type: CustomerCreditCard },
    }
  }
}

export class CustomerCardsPasses extends APIObject {
  relations () {
    return {
      passes: { type: CustomerPass },
      giftCards: { type: GiftCard },
      creditCards: { type: CustomerCreditCard },
    }
  }
}

export class CustomerCreditTransaction extends APIObject {
  relations () {
    return {
      customer: { type: Customer },
      order: { type: Order },
      type: { type: CustomerCreditTransactionType },
    }
  }
}

export class LoyaltyTransaction extends APIObject {
  relations () {
    return {
      customer: { type: Customer },
      type: { type: LoyaltyTransactionType },
    }
  }
}

export class ProductHistory extends APIObject {
  relations () {
    return {
      order: { type: Order },
      product: { type: Product },
    }
  }
}

export class CustomerStats extends APIObject {
}

export const CustomerGender = new Enum({
  none: { value: '', description: 'customer.gender.none' },
  female: { value: 'F', description: 'customer.gender.female' },
  male: { value: 'M', description: 'customer.gender.male' },
  nonBinary: { value: 'NB', description: 'customer.gender.nonBinary' },
})

export const CustomerNotifications = new Enum({
  none: { value: 0, description: 'general.none' },
  all: { value: 1, description: 'general.all' },
  email: { value: 2, description: 'general.email' },
  sms: { value: 3, description: 'general.sms' },
  emailPreferred: { value: 4, description: 'customer.filters.emailPreferred' },
  smsPreferred: { value: 5, description: 'customer.filters.smsPreferred' },
})

export const CustomerMassEmail = new Enum({
  subscribed: { value: 0, description: 'general.subscribed' },
  unsubscribed: { value: 1, description: 'general.unsubscribed' },
})

export const CustomerMassSMS = new Enum({
  subscribed: { value: 0, description: 'general.subscribed' },
  unsubscribed: { value: 1, description: 'general.unsubscribed' },
})

export const CustomerPrivacyConsent = new Enum({
  notObtained: { value: null, description: 'customer.consent.notObtained' },
  accepted: { value: true, description: 'customer.consent.accepted' },
  declined: { value: false, description: 'customer.consent.declined' },
})

export const CustomerMarketingConsent = new Enum({
  notObtained: { value: null, description: 'customer.consent.notObtained' },
  accepted: { value: true, description: 'customer.consent.accepted' },
  declined: { value: false, description: 'customer.consent.declined' },
})

export const CombinedNoteType = new Enum({
  customerNote: { value: 'customerNote', description: 'general.customer' },
  reservation: { value: 'reservation', description: 'general.appointment' },
})

export const CustomerDeleted = new Enum({
  notDeleted: { value: 0, description: 'customer.filters.notDeleted' },
  archived: { value: 1, description: 'general.archived' },
  deleted: { value: 1, description: 'general.deleted' },
})

export const CustomerCreditTransactionType = new Enum({
  manual: { value: 0, description: 'general.manual' },
  purchase: { value: 1, description: 'general.purchase' },
  payment: { value: 2, description: 'general.payment' },
  refund: { value: 3, description: 'general.refund' },
})

export const LoyaltyTransactionType = new Enum({
  manual: { value: 0, description: 'general.manual' },
  earned: { value: 1, description: 'general.earned' },
  payment: { value: 2, description: 'general.payment' },
  refund: { value: 3, description: 'general.refund' },
})

export const CustomerVerifiedVia = new Enum({
  driverLicense: { value: 1, description: 'customer.verification.driverLicense' },
  passport: { value: 2, description: 'customer.verification.passport' },
  idCard: { value: 3, description: 'customer.verification.idCard' },
  other: { value: 4, description: 'general.other' },
  sight: { value: 5, description: 'customer.verification.sight' },
})

export class CustomerFilterResponse extends APIObject {

}

export class CustomerFilters extends LocalFilterController {
  constructor (context) {
    super(Customer, { context, custom: true })

    const that = this

    const { $store, $i18n } = context

    const constructLabel = (value) => {
      const labels = value.slice(0, 3).map(({ title }) => title).join($i18n.t('general.listSeparator'))
      if (value.length > 3) {
        return $i18n.t('general.andMore', { list: labels, count: value.length - 3 })
      } else {
        return labels
      }
    }

    this.hasEmail = new Filter({
      component: 'SegmentField',
      label: $i18n.t('general.email'),
      options: [
        { label: $i18n.t('customer.filters.hasEmail'), value: 'true' },
        { label: $i18n.t('customer.filters.hasNoEmail'), value: 'false' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      matches (customers) {
        if (this.value === 'true') {
          return customers.filter((customer) => {
            return (customer.email && customer.email.length > 0)
          })
        } else if (this.value === 'false') {
          return customers.filter((customer) => {
            return !customer.email
          })
        }
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.hasPhone = new Filter({
      component: 'SegmentField',
      label: $i18n.t('customer.filters.phone'),
      options: [
        { label: $i18n.t('customer.filters.hasPhone'), value: 'true' },
        { label: $i18n.t('customer.filters.hasNoPhone'), value: 'false' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      matches (customers) {
        if (this.value === 'true') {
          return customers.filter((customer) => {
            return (customer.phone && customer.phone.length > 0)
          })
        } else if (this.value === 'false') {
          return customers.filter((customer) => {
            return !customer.phone
          })
        }
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.gender = new Filter({
      component: 'SegmentField',
      label: $i18n.t('general.gender'),
      options: [
        { label: $i18n.t('customer.gender.none'), value: '-' },
        { label: $i18n.t('customer.gender.female'), value: 'F' },
        { label: $i18n.t('customer.gender.male'), value: 'M' },
        { label: $i18n.t('customer.gender.nonBinary'), value: 'NB' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      matches (customers) {
        return customers.filter((customer) => {
          if (this.value === '-') {
            return (!customer.gender || !customer.gender.value)
          }
          return (customer.gender && customer.gender.value === this.value)
        })
      },
      valueLabel () {
        if (this.value === '-') {
          return $i18n.t('general.none')
        } else if (this.value === 'F') {
          return $i18n.t('customer.gender.female')
        } else if (this.value === 'M') {
          return $i18n.t('customer.gender.male')
        }
      },
    })

    this.dateOfBirth = new Filter({
      component: 'DateOfBirth',
      label: $i18n.t('general.dateOfBirth'),
      value: {
        day: null,
        month: null,
        year: null,
        hasDob: null,
      },
      isEmpty () {
        return (
          this.value.day == null &&
          this.value.month == null &&
          this.value.year == null &&
          this.value.hasDob == null
        )
      },
      validation () {
        const { year, month, day } = this.value
        const valid = isValid(new Date(year || 0, month || 0, day || 1))

        return valid
          ? null
          : {
              status: false,
              message: $i18n.t('customer.filters.invalidDate'),
            }
      },
      matches (customers) {
        if (this.value.hasDob === 'false') {
          return customers.filter(customer => !customer.dateOfBirth?.value)
        } else {
          const { year, day } = this.value
          let { month } = this.value

          month = month !== null ? month + 1 : null

          return customers.filter(({ dateOfBirth }) => {
            if (!dateOfBirth?.value) { return false }

            const date = dateOfBirth.value.toString()
            const customer = {
              year: Number(date.substr(0, 4)),
              month: Number(date.substr(4, 2)),
              day: Number(date.substr(6, 2)),
            }

            return (
              (day ? day === customer.day : true) &&
              (month ? month === customer.month : true) &&
              (year ? year === customer.year : true)
            )
          })
        }
      },
      valueLabel () {
        if (this.value.day || this.value.month || this.value.year) {
          const format = []
          const { year, month, day } = this.value

          if (day != null) { format.push('do') }
          if (month != null) { format.push('MMM') }
          if (year != null) { format.push('yyyy') }

          return $date.format(new Date(year || 0, month || 0, day || 1), format.join(' '))
        }
      },
      clear () {
        this.value = {
          day: null,
          month: null,
          year: null,
          hasDob: null,
        }
      },
    })

    this.hasPhoto = new Filter({
      component: 'SegmentField',
      label: $i18n.t('customer.filters.hasPhoto'),
      options: [
        { label: $i18n.t('customer.filters.hasPhoto'), value: 'true' },
        { label: $i18n.t('customer.filters.hasNoPhoto'), value: 'false' },
      ],
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      // component: 'CheckboxField',
      // label: $i18n.t('customer.filters.hasPhoto'),
      info: $i18n.t('customer.filters.hasPhotoInfo'),
      matches (customers) {
        const value = this.value === 'true'

        return customers.filter((customer) => {
          if (value) {
            return (customer.profilePhoto != null)
          }

          return (customer.profilePhoto == null)
        })
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.uploads = new Filter({
      component: 'SelectField',
      label: $i18n.t('customer.filters.uploads'),
      info: $i18n.t('customer.filters.uploadsInfo'),
      props: {
        placeholder: $i18n.t('general.any'),
      },
      options: [
        { label: $i18n.t('customer.filters.noUploads'), value: 'noUploads' },
        { label: $i18n.t('customer.filters.hasAnyUploads'), value: 'hasAnyUploads' },
        { label: $i18n.t('customer.filters.hasPhotos'), value: 'hasPhotos' },
        { label: $i18n.t('customer.filters.hasDocuments'), value: 'hasDocuments' },
        { label: $i18n.t('customer.filters.hasNoPhotos'), value: 'hasNoPhotos' },
        { label: $i18n.t('customer.filters.hasNoDocuments'), value: 'hasNoDocuments' },
      ],
      matches (customers) {
        return customers.filter((customer) => {
          const uploadPhotoCount = Number(customer?.stats?.uploadPhotoCount || 0)
          const uploadDocumentCount = Number(customer?.stats?.uploadDocumentCount || 0)
          const totalUploads = uploadPhotoCount + uploadDocumentCount

          if (this.value === 'noUploads') {
            return totalUploads <= 0
          }

          if (this.value === 'hasAnyUploads') {
            return totalUploads > 0
          }

          if (this.value === 'hasPhotos') {
            return uploadPhotoCount > 0
          }

          if (this.value === 'hasDocuments') {
            return uploadDocumentCount > 0
          }

          if (this.value === 'hasNoPhotos') {
            return uploadPhotoCount <= 0
          }

          if (this.value === 'hasNoDocuments') {
            return uploadDocumentCount <= 0
          }

          return true
        })
      },
      valueLabel () {
        const option = this.options.find(option => option.value === this.value)
        return option?.label
      },
    })

    this.created = new RemoteFilter('created', {
      label: $i18n.t('general.created'),
      info: $i18n.t('customer.filters.createdInfo'),
      value: {
        start: null,
        finish: null,
      },
      options: [
        DateRangePresets.today,
        DateRangePresets.oneWeek,
        DateRangePresets.twoWeeks,
        DateRangePresets.thisMonth,
        DateRangePresets.lastMonth,
      ],
      component: 'DateRangeOptionalField',
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      clear () {
        this.value.start = null
        this.value.finish = null
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.inactive = new RemoteFilter('inactive', {
      label: $i18n.t('general.inactive'),
      info: $i18n.t('customer.filters.inactiveInfo'),
      value: {
        start: null,
        finish: null,
      },
      options: [
        DateRangePresets.today,
        DateRangePresets.oneWeek,
        DateRangePresets.twoWeeks,
        DateRangePresets.thisMonth,
        DateRangePresets.lastMonth,
        { ...DateRangePresets.future, name: $i18n.t('customer.filters.noFutureAppointments') },
        { ...DateRangePresets.past, name: $i18n.t('customer.filters.noPastAppointments') },
      ],
      component: 'DateRangeOptionalField',
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      clear () {
        this.value.start = null
        this.value.finish = null
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.active = new RemoteFilter('active', {
      label: $i18n.t('general.active'),
      info: $i18n.t('customer.filters.activeInfo'),
      value: {
        start: null,
        finish: null,
      },
      options: [
        DateRangePresets.today,
        DateRangePresets.oneWeek,
        DateRangePresets.twoWeeks,
        DateRangePresets.thisMonth,
        DateRangePresets.lastMonth,
        { ...DateRangePresets.future, name: $i18n.t('customer.filters.hasFutureAppointments') },
        { ...DateRangePresets.past, name: $i18n.t('customer.filters.hasPastAppointments') },
      ],
      component: 'DateRangeOptionalField',
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      clear () {
        this.value = {
          start: null,
          finish: null,
        }
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.noShows = new RemoteFilter('no-shows', {
      label: $i18n.t('customer.filters.noShows'),
      info: $i18n.t('customer.filters.noShowsInfo'),
      value: {
        start: null,
        finish: null,
      },
      options: [
        DateRangePresets.today,
        DateRangePresets.oneWeek,
        DateRangePresets.twoWeeks,
        DateRangePresets.thisMonth,
        DateRangePresets.lastMonth,
      ],
      component: 'DateRangeOptionalField',
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      clear () {
        this.value = {
          start: null,
          finish: null,
        }
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.cancellations = new RemoteFilter('cancellations', {
      label: $i18n.t('customer.filters.cancellations'),
      info: $i18n.t('customer.filters.cancellationsInfo'),
      value: {
        start: null,
        finish: null,
      },
      options: [
        DateRangePresets.today,
        DateRangePresets.oneWeek,
        DateRangePresets.twoWeeks,
        DateRangePresets.thisMonth,
        DateRangePresets.lastMonth,
      ],
      component: 'DateRangeOptionalField',
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      clear () {
        this.value = {
          start: null,
          finish: null,
        }
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
    })

    this.employees = new RemoteFilter('reservations', {
      label: $i18n.t('general.employees'),
      info: $i18n.t('customer.filters.employeesInfo'),
      value: {
        employees: [],
        date: {
          start: null,
          finish: null,
        },
      },
      watch: ['date'],
      component: 'FieldsWithDateRange',
      options: {
        employees: {
          key: 'employees',
          component: 'CheckboxGroupField',
          label: $i18n.t('customer.filters.with'),
          visible: true,
          search: true,
          valueLabel: () => {
            const value = this.employees.value.employees
            return value.length ? value.map(({ displayName }) => displayName).join($i18n.t('general.listSeparator')) : $i18n.t('customer.filters.allEmployees')
          },
          items: [],
        },
      },
      valueLabel () {
        const start = (this.value.date.start) ? new Date(this.value.date.start * 1000) : null
        const finish = (this.value.date.finish) ? new Date(this.value.date.finish * 1000) : null

        const date = start || finish ? `(${(start) ? $date.format(start, $date.presets.short) : ''} » ${(finish) ? $date.format(finish, $date.presets.short) : ''})` : ''

        return `${this.options.employees.valueLabel()} ${date}`
      },
      getParams () {
        return {
          employees: this.value.employees.map(({ idEmployee }) => { return { id: idEmployee } }),
          from: this.value.date.start,
          to: this.value.date.finish,
        }
      },
      isEmpty () {
        return !this.value.date.start && !this.value.date.finish && !this.value.employees.length
      },
      clear () {
        this.value = {
          employees: [],
          date: {
            start: null,
            finish: null,
          },
        }
      },
    })

    this.services = new RemoteFilter('reservations', {
      label: $i18n.t('general.services'),
      info: $i18n.t('customer.filters.servicesInfo'),
      value: {
        services: [],
        employees: [],
        date: {
          start: null,
          finish: null,
        },
      },
      watch: ['date'],
      component: 'FieldsWithDateRange',
      options: {
        services: {
          key: 'services',
          component: 'CheckboxCategoriesField',
          label: $i18n.t('general.services'),
          visible: false,
          search: true,
          selectCategory: true,
          valueLabel: () => {
            const value = this.services.value.services

            if (value.length) {
              return constructLabel(value)
            }

            return $i18n.t('customer.filters.allServices')
          },
          items: [],
        },
        employees: {
          key: 'employees',
          component: 'CheckboxGroupField',
          label: $i18n.t('customer.filters.with'),
          visible: false,
          search: true,
          valueLabel: () => {
            const value = this.services.value.employees

            if (value.length) {
              return constructLabel(value)
            }

            return $i18n.t('customer.filters.allEmployees')
          },
          items: [],
        },
      },
      valueLabel () {
        const start = (this.value.date.start) ? new Date(this.value.date.start * 1000) : null
        const finish = (this.value.date.finish) ? new Date(this.value.date.finish * 1000) : null

        const date = start || finish ? `(${(start) ? $date.format(start, $date.presets.short) : ''} » ${(finish) ? $date.format(finish, $date.presets.short) : ''})` : ''

        return `${$i18n.t('customer.filters.servicesWithEmployees', { services: this.options.services.valueLabel(), employees: this.options.employees.valueLabel() })} ${date}`
      },
      getParams () {
        return {
          services: this.value.services.map(({ idService }) => { return { id: idService } }),
          employees: this.value.employees.map(({ idEmployee }) => { return { id: idEmployee } }),
          from: this.value.date.start,
          to: this.value.date.finish,
        }
      },
      isEmpty () {
        return !this.value.date.start && !this.value.date.finish && !this.value.services.length && !this.value.employees.length
      },
      clear () {
        this.value = {
          services: [],
          employees: [],
          date: {
            start: null,
            finish: null,
          },
        }
      },
    })

    this.segments = new Filter({
      label: $i18n.t('general.segments'),
      component: 'CheckboxGroupField',
      options: [],
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      clear () {
        this.value = []
      },
      valueLabel () {
        const value = this.value
        const labels = value.slice(0, 3).map(({ name }) => name).join($i18n.t('general.listSeparator'))
        return labels + (value.length > 3 ? ` and ${value.length - 3} more` : '')
      },
      matches (customers) {
        const selected = this.value.map(({ idSegment }) =>  idSegment)
        return customers.filter((customer) => {
          customer = customer.segments.map(({ idSegment }) =>  idSegment)
          return customer.some(item => selected.includes(item))
        })
      },
    })

    this.emailBounced = new Filter({
      label: $i18n.t('general.emailBounced'),
      info: $i18n.t('customer.filters.emailBouncedInfo'),
      component: 'ToggleField',
      valueLabel () {
        return $i18n.t('general.yes')
      },
      matches (customers) {
        return customers.filter((customer) => {
          return customer.emailBounced
        })
      },
    })

    /** todo **/
    this.duplicate = new RemoteFilter('duplicates', {
      label: $i18n.t('general.duplicate'),
      info: $i18n.t('Show customers that have more than one record'),
      component: 'ToggleField',
      getParams () {
        return []
      },
    })

    this.archived = new HybridFilter('archived', {
      label: $i18n.t('general.archived'),
      info: $i18n.t('Show archived customers'),
      component: 'SegmentField',
      value: 'notArchived',
      // force: true,
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        // { label: $i18n.t('general.all'), value: 'all' },
        { label: $i18n.t('general.archived'), value: 'archived' },
        { label: $i18n.t('Not archived'), value: 'notArchived' },
      ],
      clear () {
        this.value = 'notArchived'
      },
      valueLabel () {
        if (this.value === 'all') {
          return $i18n.t('general.all')
        }

        if (this.value === 'archived') {
          return $i18n.t('general.yes')
        }

        if (this.value === 'notArchived') {
          return null // $i18n.t('general.no')
        }

        return null
      },
      matches (customers) {
        return customers.filter((customer) => {
          return true
        })
      },
      isEmpty () {
        return false // this.value === 'notArchived'
      },
      filterRemotely () {
        let remotes = 0

        for (const key in that.remote) {
          const filter = that.remote[key]
          if (key !== 'archived' && !filter.isEmpty() && filter instanceof RemoteFilter) {
            remotes++
          }
        }

        return remotes > 0 || this.value === 'all' || this.value === 'archived'
      },
      filterLocally () {
        let remotes = 0

        for (const key in that.remote) {
          const filter = that.remote[key]
          if (key !== 'archived' && !filter.isEmpty() && filter instanceof RemoteFilter) {
            remotes++
          }
        }

        return remotes === 0 && this.value === 'notArchived'
      },
      getParams () {
        if (this.value === 'all') {
          return { archived: null }
        }

        if (this.value === 'archived') {
          return { archived: true }
        }

        if (this.value === 'notArchived') {
          return { archived: false }
        }
      },
    })

    this.allowOnlineBooking = new Filter({
      label: $i18n.t('general.onlineBooking'),
      component: 'SegmentField',
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        { label: $i18n.t('general.allowed'), value: 'true' },
        { label: $i18n.t('general.notAllowed'), value: 'false' },
      ],
      matches (customers) {
        if (this.value === 'true') {
          return customers.filter((customer) => {
            return customer.allowOnlineBooking
          })
        } else if (this.value === 'false') {
          return customers.filter((customer) => {
            return !customer.allowOnlineBooking
          })
        }
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.loyaltyPoints = new Filter({
      label: $i18n.t('general.loyaltyPoints'),
      component: 'SegmentField',
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        { label: $i18n.t('customer.filters.hasLoyaltyPoints'), value: 'true' },
        { label: $i18n.t('customer.filters.noLoyaltyPoints'), value: 'false' },
      ],
      matches (customers) {
        if (this.value === 'true') {
          return customers.filter((customer) => {
            return customer.loyaltyPointBalance > 0
          })
        } else if (this.value === 'false') {
          return customers.filter((customer) => {
            return customer.loyaltyPointBalance <= 0
          })
        }
      },
      valueLabel () {
        return (this.value === 'true') ? $i18n.t('general.yes') : $i18n.t('general.no')
      },
    })

    this.giftCards = new RemoteFilter('giftCards', {
      label: $i18n.t('general.giftCards'),
      info: $i18n.t('customer.filters.giftCardInfo'),
      component: 'SegmentField',
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        { label: $i18n.t('customer.filters.valid'), value: 'valid' },
        { label: $i18n.t('general.any'), value: 'any' },
      ],
      getParams () {
        return (this.value === 'valid') ? { valid: true } : []
      },
      valueLabel () {
        return (this.value === 'valid') ? ('customer.filters.valid') : $i18n.t('general.any')
      },
    })

    this.privacyConsent = new Filter({
      label: $i18n.t('general.privacyConsent'),
      component: 'RadioGroupField',
      options: [
        { label: $i18n.t('customer.consent.accepted'), value: 'accepted' },
        { label: $i18n.t('customer.consent.declined'), value: 'declined' },
        { label: $i18n.t('customer.consent.notObtained'), value: 'notObtained' },
      ],
      valueLabel () {
        return $i18n.t(CustomerPrivacyConsent[this.value].description)
      },
      matches (customers) {
        return customers.filter((customer) => {
          return customer.consentAccepted === CustomerPrivacyConsent[this.value]
        })
      },
    })

    this.marketingConsent = new Filter({
      label: $i18n.t('general.marketingConsent'),
      component: 'RadioGroupField',
      options: [
        { label: $i18n.t('customer.consent.accepted'), value: 'accepted' },
        { label: $i18n.t('customer.consent.declined'), value: 'declined' },
        { label: $i18n.t('customer.consent.notObtained'), value: 'notObtained' },
      ],
      valueLabel () {
        return $i18n.t(CustomerMarketingConsent[this.value].description)
      },
      matches (customers) {
        return customers.filter((customer) => {
          return customer.marketingConsentAccepted === CustomerMarketingConsent[this.value]
        })
      },
    })

    this.massEmailSubscription = new Filter({
      label: $i18n.t('customer.filters.massEmailSubscription'),
      component: 'SegmentField',
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        { label: $i18n.t('general.subscribed'), value: 'subscribed' },
        { label: $i18n.t('general.unsubscribed'), value: 'unsubscribed' },
      ],
      valueLabel () {
        return $i18n.t(CustomerMassEmail[this.value].description)
      },
      matches (customers) {
        return customers.filter((customer) => {
          return customer.massEmailUnsubscribed === CustomerMassEmail[this.value]
        })
      },
    })

    this.massSMSSubscription = new Filter({
      label: $i18n.t('customer.filters.massSMSSubscription'),
      component: 'SegmentField',
      props: {
        outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2',
      },
      options: [
        { label: $i18n.t('general.subscribed'), value: 'subscribed' },
        { label: $i18n.t('general.unsubscribed'), value: 'unsubscribed' },
      ],
      valueLabel () {
        return $i18n.t(CustomerMassSMS[this.value].description)
      },
      matches (customers) {
        return customers.filter((customer) => {
          return customer.massSMSUnsubscribed === CustomerMassSMS[this.value]
        })
      },
    })

    this.formsFilled = new RemoteFilter('forms', {
      label: $i18n.t('customer.filters.formsFilled'),
      info: $i18n.t('customer.filters.formsFilledInfo'),
      component: 'CheckboxGroupField',
      options: [],
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      valueLabel () {
        const value = this.value

        if (value.length) {
          const labels = value.slice(0, 3).map(({ title }) => title).join($i18n.t('general.listSeparator'))
          return labels + (value.length > 3 ? ` and ${value.length - 3} more` : '')
        }

        return $i18n.t('customer.filters.allForms')
      },
      getParams () {
        return {
          forms: this.value.map(({ idForm }) => idForm),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.formsRequested = new RemoteFilter('requested-forms', {
      label: $i18n.t('customer.filters.formsRequested'),
      info: $i18n.t('customer.filters.formsRequestedInfo'),
      component: 'CheckboxGroupField',
      options: [],
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      valueLabel () {
        const value = this.value

        if (value.length) {
          return constructLabel(value)
        }

        return $i18n.t('customer.filters.allForms')
      },
      getParams () {
        return {
          forms: this.value.map(({ idForm }) => idForm),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.formsNotRequested = new RemoteFilter('no-forms', {
      label: $i18n.t('customer.filters.formsNotRequested'),
      info: $i18n.t('customer.filters.formsNotRequestedInfo'),
      component: 'CheckboxGroupField',
      options: [],
      props: {
        search: true,
        class: 'border rounded overflow-hidden bg-gray-100 dark:bg-gray-900 h-80 dark:border-gray-800 px-1',
      },
      valueLabel () {
        const value = this.value

        if (value.length) {
          return constructLabel(value)
        }

        return $i18n.t('customer.filters.allForms')
      },
      getParams () {
        return {
          forms: this.value.map(({ idForm }) => idForm),
        }
      },
      clear () {
        this.value = []
      },
    })

    this.products = new RemoteFilter('products', {
      label: $i18n.t('general.products'),
      info: $i18n.t('customer.filters.productsInfo'),
      component: 'FieldsWithDateRange',
      watch: ['date'],
      options: {
        products: {
          key: 'products',
          component: 'CheckboxCategoriesField',
          label: $i18n.t('general.products'),
          visible: false,
          search: true,
          selectCategory: true,
          valueLabel: () => {
            const value = this.products.value.products

            if (value.length) {
              return constructLabel(value)
            }

            return $i18n.t('product.all')
          },
          items: [],
        },
      },
      value: {
        products: [],
        date: {
          start: null,
          finish: null,
        },
      },
      valueLabel () {
        const start = (this.value.date.start) ? new Date(this.value.date.start * 1000) : null
        const finish = (this.value.date.finish) ? new Date(this.value.date.finish * 1000) : null

        const date = start || finish ? `(${(start) ? $date.format(start, $date.presets.short) : ''} » ${(finish) ? $date.format(finish, $date.presets.short) : ''})` : ''

        return `${this.options.products.valueLabel()} ${date}`
      },
      getParams () {
        return {
          products: this.value.products.map(({ idProduct }) => { return { id: idProduct } }),
          from: this.value.date.start,
          to: this.value.date.finish,
        }
      },
      isEmpty () {
        return !this.value.date.start && !this.value.date.finish && !this.value.products.length
      },
      clear () {
        this.value = {
          products: [],
          date: {
            start: null,
            finish: null,
          },
        }
      },
    })

    this.giftCardsPurchased = new RemoteFilter('giftCardPurchased', {
      label: $i18n.t('general.giftCards'),
      info: $i18n.t('customer.filters.hasPurchasedGiftCard'),
      component: 'DateRangeOptionalField',
      value: {
        start: null,
        finish: null,
      },
      valueLabel () {
        const start = (this.value.start) ? new Date(this.value.start * 1000) : null
        const finish = (this.value.finish) ? new Date(this.value.finish * 1000) : null

        return ((start) ? $date.format(start, $date.presets.short) : '') + ' » ' + ((finish) ? $date.format(finish, $date.presets.short) : '')
      },
      getParams () {
        return {
          from: this.value.start,
          to: this.value.finish,
        }
      },
      isEmpty () {
        return !this.value.start && !this.value.finish
      },
      clear () {
        this.value = {
          start: null,
          finish: null,
        }
      },
    })

    this.passes = new RemoteFilter('passes', {
      label: $i18n.t('general.passes'),
      info: $i18n.t('customer.filters.hasPurchasedSelectedPasses'),
      component: 'FieldsWithDateRange',
      watch: ['date'],
      options: {
        passes: {
          key: 'passes',
          component: 'CheckboxGroupField',
          label: $i18n.t('general.passes'),
          visible: false,
          search: true,
          selectCategory: true,
          valueLabel: () => {
            const value = this.passes.value.passes

            if (value.length) {
              const labels = value.slice(0, 3).map(({ name }) => name).join($i18n.t('general.listSeparator'))
              return labels + (value.length > 3 ? ` and ${value.length - 3} more` : '')
            }

            return $i18n.t('customer.filters.allPasses')
          },
          items: [],
        },
      },
      value: {
        passes: [],
        date: {
          start: null,
          finish: null,
        },
      },
      valueLabel () {
        const start = (this.value.date.start) ? new Date(this.value.date.start * 1000) : null
        const finish = (this.value.date.finish) ? new Date(this.value.date.finish * 1000) : null

        const date = start || finish ? `(${(start) ? $date.format(start, $date.presets.short) : ''} » ${(finish) ? $date.format(finish, $date.presets.short) : ''})` : ''

        return `${this.options.passes.valueLabel()} ${date}`
      },
      getParams () {
        return {
          passes: this.value.passes.map(({ idPass }) => { return { id: idPass } }),
          from: this.value.date.start,
          to: this.value.date.finish,
        }
      },
      isEmpty () {
        return !this.value.date.start && !this.value.date.finish && !this.value.passes.length
      },
      clear () {
        this.value = {
          passes: [],
          date: {
            start: null,
            finish: null,
          },
        }
      },
    })


    this.formOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['form/all']).map(form => ({
            value: form,
            text: form.title,
            info: '#' + form.idForm,
          }))
          : []
      },
      set: (options) => {
        this.formsFilled.options = options
        this.formsRequested.options = options
        this.formsNotRequested.options = options
      },
    })

    this.employeeOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['employee/all'])
            .filter(({ type }) => type === EmployeeEmployeeType.employee)
            .map(employee => ({
              value: employee,
              text: employee.displayName,
              info: employee.type ? $i18n.t(employee.type.description) : $i18n.t('general.employee'),
              image: employee.images ? employee.images.square : null,
            }))
          : []
      },
      set: (options) => {
        this.employees.options.employees.items = options
        this.services.options.employees.items = options
      },
    })

    this.serviceOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['service/categorised']).map(category => ({
            label: category.name || '',
            options: category.services?.map(service => ({
              value: service,
              text: service.title,
            })),
          }))
          : []
      },
      set: (options) => {
        this.services.options.services.items = options
      },
    })

    this.segmentOptions = new FilterOptions({
      map () {
        return $store
          ? Object.values($store.getters['segment/all'])
            .map(segment => ({
              value: segment,
              text: segment.name,
              colour: segment.hexColour,
            }))
          : []
      },
      set: (options) => {
        this.segments.options = options
      },
    })

    this.productOptions = new FilterOptions({
      map () {
        const products = $store ? $store.getters['product/all'] : null
        return products
          ? Object.values(products).map(category => ({
            label: category.category ? category.category.name : $i18n.t('general.other'),
            options: category.products.map(product => ({
              value: product,
              text: product.name,
            })),
          }))
          : []
      },
      set: (options) => {
        this.products.options.products.items = options
      },
    })

    this.passOptions = new FilterOptions({
      async map () {
        const passes = await Pass.listAll()
        return passes
          .map(pass => ({
            value: pass,
            text: pass.name,
          }))
      },
      set: (options) => {
        this.passes.options.passes.items = options
      },
    })

    this.search = new Filter({
      label: $i18n.t('general.search'),
      force: true,
      matches (customers) {
        const fuse = new Fuse(customers, {
          keys: [
            {
              name: 'displayName',
              weight: 2,
            },
            'email',
            'phone',
            'address',
            'city',
            'postcode',
            'suburb',
            'region',
            'homePhone',
            'company',
          ],
          threshold: 0.3,
        })

        const results = fuse.search(this.value)
        return results.map(({ item }) => item)
      },
    })

    this.categorised = [
      new FilterCategory({
        label: $i18n.t('customer.filters.personal'),
        filters: [this.hasEmail, this.hasPhone, this.gender, this.dateOfBirth, this.hasPhoto, this.uploads],
      }),
      new FilterCategory({
        label: $i18n.t('general.appointments'),
        filters: [this.services, this.inactive, this.active, this.noShows, this.cancellations],
      }),
      new FilterCategory({
        label: $i18n.t('general.segments'),
        filters: [this.segments],
        features: ['segments'],
      }),
      new FilterCategory({
        label: $i18n.t('general.marketing'),
        filters: [this.loyaltyPoints, this.giftCards, this.allowOnlineBooking, this.privacyConsent, this.marketingConsent, this.massEmailSubscription, this.massSMSSubscription, this.emailBounced],
      }),
      new FilterCategory({
        label: $i18n.t('general.forms'),
        filters: [this.formsFilled, this.formsRequested, this.formsNotRequested],
        features: ['forms'],
      }),
      new FilterCategory({
        label: $i18n.t('general.products'),
        filters: [this.products, this.giftCardsPurchased, this.passes],
      }),
      new FilterCategory({
        label: $i18n.t('general.customFields'),
        filters: this.custom,
        features: ['custom-fields'],
      }),
      new FilterCategory({
        label: $i18n.t('general.manage'),
        filters: [this.duplicate, this.archived, this.created],
      }),
    ]

    this.marketingCategorised = [
      new FilterCategory({
        label: $i18n.t('customer.filters.personal'),
        filters: [this.hasEmail, this.hasPhone, this.gender, this.dateOfBirth, this.hasPhoto, this.uploads],
      }),
      new FilterCategory({
        label: $i18n.t('general.appointments'),
        filters: [this.services, this.inactive, this.active, this.noShows, this.cancellations],
      }),
      new FilterCategory({
        label: $i18n.t('general.segments'),
        filters: [this.segments],
        features: ['segments'],
      }),
      new FilterCategory({
        label: $i18n.t('general.marketing'),
        filters: [this.loyaltyPoints, this.giftCards, this.allowOnlineBooking, this.privacyConsent, this.marketingConsent, this.massEmailSubscription, this.massSMSSubscription],
      }),
      new FilterCategory({
        label: $i18n.t('general.forms'),
        filters: [this.formsFilled, this.formsRequested, this.formsNotRequested],
        features: ['forms'],
      }),
      new FilterCategory({
        label: $i18n.t('general.products'),
        filters: [this.products, this.giftCardsPurchased, this.passes],
      }),
      new FilterCategory({
        label: $i18n.t('general.customFields'),
        filters: this.custom,
        features: ['custom-fields'],
      }),
      new FilterCategory({
        label: $i18n.t('general.manage'),
        filters: [this.duplicate, this.created],
      }),
    ]
  }
}
